import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileText } from "lucide-react";
import { INVOICE_STATUSES, INVOICE_STATUS_DEFINITIONS, type InvoiceStatus } from "@laundrypro/shared";
import {
  Badge, Button, Card, EmptyState, PageHeader, Skeleton,
  Table, Td, Th, THead, Tr,
} from "../components/ui";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, dateTime, money } from "../lib/format";

interface InvoiceListItem {
  id: string;
  invoiceRef: string | null;
  status: InvoiceStatus;
  customerName: string | null;
  totalMinor: number;
  amountPaidMinor: number;
  balanceDueMinor: number;
  issuedAt: string | null;
  createdAt: string;
}

/** The settlement states the API filters on — the derived ones are not stored. */
const FILTERS = [null, "DRAFT", "ISSUED", "VOIDED"] as const;

export function InvoicesPage() {
  const { language } = useBranding();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [items, setItems] = useState<InvoiceListItem[] | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setItems(null);
    try {
      const query = filter ? `?status=${filter}&limit=100` : "?limit=100";
      const res = await apiFetch<{ items: InvoiceListItem[] }>(`/invoices${query}`);
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الفواتير." : "Couldn't load invoices.");
    }
  }, [filter, ar]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "الفواتير" : "Invoices"}
        subtitle={ar ? "المستندات الضريبية الصادرة والمسودات." : "Issued tax documents and drafts."}
      />

      {/* Filter chips — the same restrained treatment the orders list uses. */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f;
          const label = f
            ? ar
              ? INVOICE_STATUS_DEFINITIONS[f].labelAr
              : INVOICE_STATUS_DEFINITIONS[f].labelEn
            : ar
              ? "الكل"
              : "All";
          return (
            <button
              key={f ?? "all"}
              type="button"
              onClick={() => setFilter(f)}
              className={`h-9 rounded-lg border px-3.5 text-xs font-medium transition-colors ${
                active
                  ? "border-ink-900 bg-ink-900 text-white"
                  : "border-line bg-card text-ink-600 hover:border-line-strong hover:bg-ink-50"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {error ? (
        <Card>
          <EmptyState
            icon={<AlertTriangle className="size-6" />}
            title={ar ? "تعذّر تحميل الفواتير" : "Couldn't load invoices"}
            description={error}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>}
          />
        </Card>
      ) : items === null ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="size-6" />}
            title={ar ? "لا توجد فواتير" : "No invoices yet"}
            description={
              ar
                ? "تُنشأ الفواتير من الطلبات. افتح طلبًا وأصدر فاتورته."
                : "Invoices are created from orders. Open an order and raise its invoice."
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="p-5 sm:p-6">
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "الرقم" : "Number"}</Th>
                  <Th>{ar ? "العميل" : "Customer"}</Th>
                  <Th>{ar ? "الحالة" : "Status"}</Th>
                  <Th>{ar ? "الإجمالي" : "Total"}</Th>
                  <Th>{ar ? "المستحق" : "Due"}</Th>
                  <Th>{ar ? "التاريخ" : "Date"}</Th>
                </tr>
              </THead>
              <tbody>
                {items.map((inv) => {
                  const def = INVOICE_STATUS_DEFINITIONS[inv.status];
                  return (
                    <Tr key={inv.id}>
                      <Td>
                        <Link
                          to={`/invoices/${inv.id}`}
                          className="tabular-nums font-medium text-accent-600 hover:underline"
                        >
                          {inv.invoiceRef ?? (ar ? "مسودة" : "Draft")}
                        </Link>
                      </Td>
                      <Td className="text-ink-900">{inv.customerName ?? "—"}</Td>
                      <Td>
                        <Badge tone={def.tone}>{ar ? def.labelAr : def.labelEn}</Badge>
                      </Td>
                      <Td className="tabular-nums">{money(inv.totalMinor)} {currency}</Td>
                      <Td
                        className={`tabular-nums ${inv.balanceDueMinor > 0 ? "font-medium text-warning" : "text-muted-foreground"}`}
                      >
                        {money(inv.balanceDueMinor)}
                      </Td>
                      <Td className="tabular-nums text-muted-foreground">
                        {dateTime(inv.issuedAt ?? inv.createdAt, language)}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
